import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Shield,
  UserX,
  ShieldCheck,
  XCircle,
  DollarSign,
  MessageSquare,
  Gavel,
  Copyright,
  Scale,
  CreditCard,
  Ban,
  RotateCcw,
  Store,
  Truck,
  Cookie,
  Users,
  Database,
  Settings,
  FileSignature,
  LucideIcon,
} from "lucide-react";

interface PolicyCard {
  title: string;
  description: string;
  path: string;
  icon: LucideIcon;
  category: "core" | "business" | "user" | "compliance";
}

const DocumentsPage: React.FC = () => {
  const policies: PolicyCard[] = [
    // Core Legal Documents
    {
      title: "Terms & Conditions",
      description:
        "Comprehensive terms governing the use of Baakas platform, including user rights, responsibilities, and platform rules.",
      path: "/terms",
      icon: FileText,
      category: "core",
    },
    {
      title: "Privacy Policy",
      description:
        "Detailed information about how we collect, use, store, and protect your personal information and data.",
      path: "/privacy",
      icon: Shield,
      category: "core",
    },

    // Business Policies
    {
      title: "Commission & Fee Structure",
      description:
        "Complete breakdown of all fees, commissions, and charges applicable to buyers and sellers on our platform.",
      path: "/commission-fee-structure",
      icon: DollarSign,
      category: "business",
    },
    {
      title: "Payment Policy",
      description:
        "Guidelines for payment processing, accepted methods, security measures, and transaction procedures.",
      path: "/payment-policy",
      icon: CreditCard,
      category: "business",
    },
    {
      title: "Return & Refund Policy",
      description:
        "Comprehensive policy covering returns, refunds, exchanges, and related procedures for buyers and sellers.",
      path: "/refund-policy",
      icon: RotateCcw,
      category: "business",
    },
    {
      title: "Shipping & Delivery Policy",
      description:
        "Guidelines for shipping, delivery timelines, responsibilities, and procedures for domestic and international orders.",
      path: "/shipping-policy",
      icon: Truck,
      category: "business",
    },
    {
      title: "Cancellation Policy",
      description:
        "Rules and procedures for order cancellations, including timelines, conditions, and refund implications.",
      path: "/cancellation-policy",
      icon: XCircle,
      category: "business",
    },

    // User Policies
    {
      title: "Seller Policy",
      description:
        "Comprehensive guidelines for sellers including listing requirements, quality standards, and seller responsibilities.",
      path: "/seller-policy",
      icon: Store,
      category: "user",
    },
    {
      title: "Buyer Protection Policy",
      description:
        "Protection measures and procedures for buyers, including dispute resolution and guarantee coverage.",
      path: "/buyer-protection-policy",
      icon: ShieldCheck,
      category: "user",
    },
    {
      title: "Account Suspension Policy",
      description:
        "Conditions and procedures for account suspension, including violations that may lead to suspension and appeal processes.",
      path: "/account-suspension-policy",
      icon: UserX,
      category: "user",
    },
    {
      title: "Content & Review Policy",
      description:
        "Guidelines for user-generated content, product reviews, ratings, and content moderation procedures.",
      path: "/content-review-policy",
      icon: MessageSquare,
      category: "user",
    },
    {
      title: "Code of Conduct",
      description:
        "Expected behavior standards for all platform users, including community guidelines and ethical practices.",
      path: "/code-of-conduct",
      icon: Users,
      category: "user",
    },

    // Compliance & Legal
    {
      title: "Legal Compliance Policy",
      description:
        "Our commitment to legal compliance, regulatory requirements, and industry standards.",
      path: "/legal-compliance-policy",
      icon: Scale,
      category: "compliance",
    },
    {
      title: "Intellectual Property Policy",
      description:
        "Protection of intellectual property rights, trademark policies, and procedures for reporting infringement.",
      path: "/intellectual-property-policy",
      icon: Copyright,
      category: "compliance",
    },
    {
      title: "Prohibited Items Policy",
      description:
        "Comprehensive list of prohibited and restricted items that cannot be sold on our platform.",
      path: "/prohibited-items-policy",
      icon: Ban,
      category: "compliance",
    },
    {
      title: "Dispute Resolution Policy",
      description:
        "Procedures for resolving disputes between users, including mediation and arbitration processes.",
      path: "/dispute-resolution-policy",
      icon: Gavel,
      category: "compliance",
    },
    {
      title: "Cookie Policy",
      description:
        "Information about our use of cookies, tracking technologies, and your choices regarding data collection.",
      path: "/cookie-policy",
      icon: Cookie,
      category: "compliance",
    },
    {
      title: "Data Processing Agreement",
      description:
        "Terms for data processing activities, including GDPR compliance and data handling procedures.",
      path: "/data-processing-agreement",
      icon: Database,
      category: "compliance",
    },
    {
      title: "Service Level Agreement",
      description:
        "Our commitment to service quality, uptime guarantees, and performance standards.",
      path: "/service-level-agreement",
      icon: Settings,
      category: "compliance",
    },
    {
      title: "General Agreements",
      description:
        "Additional legal agreements and contracts related to specific services and partnerships.",
      path: "/agreements",
      icon: FileSignature,
      category: "compliance",
    },
  ];

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case "core":
        return "Core Legal Documents";
      case "business":
        return "Business & Transaction Policies";
      case "user":
        return "User Guidelines & Policies";
      case "compliance":
        return "Compliance & Legal Framework";
      default:
        return "Other Documents";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "core":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "business":
        return "text-green-600 bg-green-50 border-green-200";
      case "user":
        return "text-purple-600 bg-purple-50 border-purple-200";
      case "compliance":
        return "text-orange-600 bg-orange-50 border-orange-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const groupedPolicies = policies.reduce((acc, policy) => {
    if (!acc[policy.category]) {
      acc[policy.category] = [];
    }
    acc[policy.category].push(policy);
    return acc;
  }, {} as Record<string, PolicyCard[]>);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <FileText className="text-primary-500" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">Legal Documents</h1>
        </div>
        <p className="text-lg text-gray-600 max-w-3xl">
          Welcome to Baakas Legal Center. Here you can find all our policies,
          terms, and legal documents. These documents outline how our platform
          operates, your rights and responsibilities, and our commitment to
          transparency.
        </p>
      </div>

      {/* Policy Categories */}
      {Object.entries(groupedPolicies).map(([category, categoryPolicies]) => (
        <div key={category} className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            {getCategoryTitle(category)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryPolicies.map((policy) => {
              const Icon = policy.icon;
              return (
                <Link
                  key={policy.path}
                  to={policy.path}
                  className="group block bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-300"
                >
                  <div className="p-6">
                    <div className="flex items-start space-x-4">
                      <div
                        className={`flex-shrink-0 p-3 rounded-lg ${getCategoryColor(
                          category
                        )}`}
                      >
                        <Icon size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {policy.title}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                          {policy.description}
                        </p>
                        <div className="mt-4">
                          <span className="text-sm font-medium text-primary-600 group-hover:text-primary-700">
                            Read document →
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      {/* Footer Information */}
      <div className="mt-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Important Information
        </h3>
        <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Last Updated</h4>
            <p>
              All documents are regularly reviewed and updated. The last update
              date is displayed at the top of each document.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Questions?</h4>
            <p>
              If you have questions about any of these documents, please contact
              our legal team through our support channels.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">
              Document Features
            </h4>
            <p>
              Each document includes a table of contents, search functionality,
              and options to print or export as PDF.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Compliance</h4>
            <p>
              Our policies comply with applicable laws and regulations,
              including GDPR, consumer protection laws, and industry standards.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
